import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation} from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormArray, FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { getValidators } from '../../utils/validators.utils';
import { ChipsService } from './chips.service';
import { ActiveFormService } from '../../../modules/flow/services/active-form.service';
import { swalMini } from '../../utils/utils';

@Component({
  selector: 'clickatell-chip',
  templateUrl: './clickatell-chip.component.html',
  styleUrls: ['./clickatell-chip.component.css'],
})
export class ClickatellChipComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() name: string;
  @Input() path: string;
  @Input() parentFormGroup: FormGroup;
  @Input() index: number;
  @Input() readonly: boolean;
  @Input() maxKeywords: boolean;
  @Input() maxLength: number;
  @Input() minLength: number = 0;

  // only to get the label;
  // When adding validation validation
  @Input() field: any;

  @Output() focus = new EventEmitter();

  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  objectKeys = Object.keys;

  // doValidation = false;

  constructor(
    private formBuilder: FormBuilder,
    public chipsService: ChipsService,
    private activeFormService: ActiveFormService
  ) {}

  ngOnInit() {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.chipsService.resetChipsData();
  }

  initForm(): void {
    this.chipsService.setDoValidation = this.field.validators && this.field.validators.duplicate;
    let chipsList: any =
      this.form.get(this.name.toString()) && this.form.get(this.name.toString()).value;
    const nodeData: any = this.activeFormService.getActiveFormNode();
    if (this.name === 'resendInputs' && !chipsList.length && nodeData) {
      chipsList = nodeData.menu.otp.resendInputs;
    }
    if (
      this.name === 'scope' &&
      !chipsList.length &&
      nodeData &&
      nodeData.menu.menuParams &&
      nodeData.menu.menuParams.oauth2
    ) {
      chipsList = nodeData.menu.menuParams.oauth2.scope;
    }
    this.setupForm(chipsList);
    this.chipsService.addChips(chipsList);
  }

  private setupForm(chipsList: any) {
    const validators = getValidators(this.field);
    if (!chipsList) {
      this.form.controls[this.name] = this.formBuilder.array([], validators);
    } else {
      this.form.controls[this.name] = this.formBuilder.array(chipsList, validators);
    }
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;
    if (!value.startsWith('[[') && !value.endsWith(']]') && (value || '').trim()) {
      this.chipsService.addValidatorValues(value.trim());
      const control = (<FormArray>this.form.controls[this.name]) as FormArray;
      control.push(this.formBuilder.control(value.trim()));
      this.onChipsChange();
      this.setCustomValidator(true);
    }
    if(value.startsWith('[[') || value.endsWith(']]')){
      swalMini({
        title: 'The value you enter here cannot be a variable.',
        type: 'error',
      });
    }
    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  remove(index: number, value: any): void {
    const control = (<FormArray>this.form.controls[this.name]) as FormArray;
    control.removeAt(index);
    this.chipsService.removeValidatorValues(value);
    this.setCustomValidator();
    this.onChipsChange();
  }

  setCustomValidator(showWarning?: boolean) {
    this.form.setValidators(duplicateValidator(this.chipsService.isDuplicate, null, showWarning));
    this.form.updateValueAndValidity();
  }

  private onChipsChange() {
    this.form.controls[this.name].markAsDirty();
    this.form.updateValueAndValidity();
  }

  onFocus(element: any) {
    this.activeFormService.setFocusStatus(this.name, this.path);
    const focusElement = {
      name: this.name,
      element: element,
    };
    this.focus.emit(focusElement);
  }

  isDisabled(form, name, field) {
    const item = form.get(name.toString());
    if (item && item.value && field.validators && field.validators.maxLength) {
      return item.value.length >= field.validators.maxLength;
    }
    return false;
  }

  checkMaxKeywordsLength() {
    return `Max ${this.maxLength ? this.maxLength : '5'} keywords`;
  }
}

function duplicateValidator(
  duplicatesFound: Boolean,
  message?: string,
  showWarning?: boolean
): ValidatorFn {
  if (!message) {
    message = 'Conflicting keywords found or option number conflicting with keyword';
  }
  if (duplicatesFound && showWarning) {
    swalMini({
      title: message,
      type: 'error',
    });
  }
  return (): { [key: string]: any } | null => {
    return duplicatesFound ? { duplicateValueFound: message } : null;
  };
}
